import { useMemo } from "react";
import { useGetLeaseDuration } from "../../../../BookingForm/hooks/useGetLeaseDuration";
import { usePreviousTermTorRulesSnapshot } from "../../../../BookingForm/components/Commercial/tor-rules";
import {
  buildWhatsChangingRows,
  formatContractTerm,
  type WhatsChangingRow,
} from "./buildWhatsChangingRows";
import type { Booking } from "../../../../../../types/bookings";

type RenewalBookingDetails = Booking;

const getYear1AnnualRent = (booking: RenewalBookingDetails, useParent: boolean): number | undefined => {
  const leaseTerms = useParent
    ? booking.parentLeaseTerms ?? booking.leaseTerms
    : booking.leaseTerms;

  if (leaseTerms?.contractAmount != null) return Number(leaseTerms.contractAmount);

  const yearOnePlan =
    booking.paymentPlan?.find((plan) => plan.noOfYear === 1) ?? booking.paymentPlan?.[0];

  if (useParent) {
    if (yearOnePlan?.oldAnnualRent != null) return Number(yearOnePlan.oldAnnualRent);
    if (yearOnePlan?.annualRent != null && booking.parentLeaseTerms) return Number(yearOnePlan.annualRent);
    const parentTerms = booking.parentBookingTerms ?? booking.bookingTerms;
    return parentTerms?.annualBaseRent ?? parentTerms?.renewalAmount;
  }

  if (yearOnePlan?.annualRent != null) return Number(yearOnePlan.annualRent);

  const renewalTerms = booking.bookingTerms;
  return renewalTerms?.renewalAmount ?? renewalTerms?.annualBaseRent;
};

export const useWhatsChangingComparison = (bookingDetails: RenewalBookingDetails) => {
  const expiringLeaseTerms = bookingDetails?.parentLeaseTerms;
  const renewalLeaseTerms = bookingDetails?.leaseTerms;
  const expiringBookingTerms = bookingDetails?.parentBookingTerms;
  const renewalBookingTerms = bookingDetails?.bookingTerms;

  const { previousTermValues, hasPreviousRules } = usePreviousTermTorRulesSnapshot({
    leaseId: bookingDetails?.leaseId,
    source: {
      parentTorRules: bookingDetails?.parentTorRules,
      parentTorRuleCondition: bookingDetails?.parentTorRuleCondition,
      torRules: bookingDetails?.torRules,
      torRuleCondition: bookingDetails?.torRuleCondition,
    },
  });

  const expiringTorRules =
    bookingDetails?.parentTorRules;
  const expiringTorRuleCondition =
    bookingDetails?.parentTorRuleCondition ??
    (hasPreviousRules ? previousTermValues.torRuleCondition : bookingDetails?.torRuleCondition);

  const expiringDuration = useGetLeaseDuration(expiringLeaseTerms?.leaseStartDate, expiringLeaseTerms?.leaseEndDate);
  const renewalDuration = useGetLeaseDuration(renewalLeaseTerms?.leaseStartDate, renewalLeaseTerms?.leaseEndDate);

  const rows = useMemo((): WhatsChangingRow[] => {
    const builtRows = buildWhatsChangingRows({
      expiringLeaseTerms,
      renewalLeaseTerms,
      expiringBookingTerms,
      renewalBookingTerms,
      expiringTorRules,
      expiringTorRuleCondition,
      renewalTorRules: bookingDetails?.torRules,
      renewalTorRuleCondition: bookingDetails?.torRuleCondition,
      expiringYear1Rent: getYear1AnnualRent(bookingDetails, true),
      renewalYear1Rent: getYear1AnnualRent(bookingDetails, false),
      torType: bookingDetails?.torType,
    });

    return builtRows.map((row) => {
      if (row.key !== "contract-term") return row;

      const expiringFromDuration = expiringDuration.years
        ? formatContractTerm(expiringDuration.years, "years")
        : expiringDuration.label;
      const renewalFromDuration = renewalDuration.years
        ? formatContractTerm(renewalDuration.years, "years")
        : renewalDuration.label;

      const expiring =
        row.expiring !== "--" ? row.expiring : expiringDuration.label ? expiringFromDuration : row.expiring;
      const renewal =
        row.renewal !== "--" ? row.renewal : renewalDuration.label ? renewalFromDuration : row.renewal;

      return {
        ...row,
        expiring,
        renewal,
        status: expiring.trim().toLowerCase() === renewal.trim().toLowerCase() ? "same" : "changed",
      };
    });
  }, [
    bookingDetails,
    expiringBookingTerms,
    expiringDuration.label,
    expiringDuration.years,
    expiringLeaseTerms,
    expiringTorRuleCondition,
    expiringTorRules,
    renewalBookingTerms,
    renewalDuration.label,
    renewalDuration.years,
    renewalLeaseTerms,
  ]);

  return { rows };
};
